import * as stylex from '@stylexjs/stylex';

/**
 * KEYFRAMES
 */
const floatUp = stylex.keyframes({
    '0%': { transform: 'translateY(20px)', opacity: 0 },
    '100%': { transform: 'translateY(0)', opacity: 1 },
});

const pulse = stylex.keyframes({
    '0%': { transform: 'scale(1)', opacity: 0.75 },
    '50%': { transform: 'scale(1.1)', opacity: 1 },
    '100%': { transform: 'scale(1)', opacity: 0.75 },
});



const fadeSlideIn = stylex.keyframes({
    '0%': { opacity: 0, transform: 'translateY(30px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
});

const ripple = stylex.keyframes({
    '0%': { transform: 'scale(0)', opacity: 0.6 },
    '50%': { opacity: 0.4 },
    '100%': { transform: 'scale(1.5)', opacity: 0 },
});

const spin = stylex.keyframes({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
});

/**
 * MEDIA QUERIES
 */
const MOBILE = '@media (max-width: 600px)';
const TABLET = '@media (max-width: 768px)';
const SMALL_MOBILE = '@media (max-width: 480px)';


/**
 * STYLES
 */
export const styles = stylex.create({
    pageContainer: {
        maxWidth: 'calc(95vw - var(--sidebar-width))',
        marginInline: 'auto',
        padding: 0,
        [TABLET]: {
            maxWidth: '95vw',
        },
        [SMALL_MOBILE]: {
            maxWidth: '100vw',
        }
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: {
            default: 'var(--text-secondary)',
            ':hover': 'var(--primary)',
        },
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transitionProperty: 'color',
        transitionDuration: 'var(--transition-fast)',
    },
    mouseAnimation: {
        position: 'absolute',
        pointerEvents: 'none',
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        '::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundImage: 'radial-gradient(circle, var(--primary-light) 0%, transparent 70%)',
            transform: 'translate(-50%, -50%)',
            animationName: ripple,
            animationDuration: '2s',
            animationIterationCount: 'infinite',
            animationTimingFunction: 'cubic-bezier(0.3, 0, 0.2, 1)',
        },
        '::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundImage: 'radial-gradient(circle, var(--accent-light) 0%, transparent 70%)',
            transform: 'translate(-50%, -50%)',
            animationName: ripple,
            animationDuration: '2s',
            animationIterationCount: 'infinite',
            animationTimingFunction: 'cubic-bezier(0.3, 0, 0.2, 1)',
            animationDelay: '0.5s',
        }
    },
    mouseParticle: {
        position: 'absolute',
        pointerEvents: 'none',
        borderRadius: '50%',
        zIndex: 5,
        opacity: 0.8,
        filter: 'blur(1px)',
        transitionProperty: 'opacity, transform',
        transitionDuration: '0.5s',
        transitionTimingFunction: 'ease',
    },
    mediaArea: {
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--bg-card)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        position: 'relative',
        animationName: floatUp,
        animationDuration: '0.8s',
        animationTimingFunction: 'ease-out',
        zIndex: 1,
    },
    projectDetailsArea: {
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--bg-card)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        position: 'relative',
        animationName: floatUp,
        animationDuration: '0.8s',
        animationTimingFunction: 'ease-out',
        zIndex: 1,
    },
    contentGrid: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(2, 1fr)',
            [MOBILE]: '1fr',
        },
        gap: 'var(--spacing-md)',
        width: '100%',
    },
    gridItem: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
    },
    twoColumnLayout: {
        display: 'flex',
        gap: 'var(--spacing-md)',
        width: '100%',
    },
    columnHalf: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-md)',
        width: '50%',
    },
    projectDates: {
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text-secondary)',
        marginBottom: '10px',
    },
    dateInfo: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '5px',
    },
    dateValue: {
        color: 'var(--text-primary)',
        fontWeight: 500,
    },
    storyContainer: {
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--radius-md)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        overflow: 'hidden',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
    },
    storyTitle: {
        color: 'var(--text-primary)',
        fontSize: 'var(--font-size-xl)',
        fontWeight: 600,
        lineHeight: 1.3,
        position: 'relative',
    },
    sectionHeader: {
        backgroundImage: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-hover) 100%)',
        paddingBlock: 'var(--spacing-md)',
        paddingInline: 'var(--spacing-lg)',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'var(--border)',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    sectionHeaderTitle: {
        margin: 0,
        fontSize: 'var(--font-size-lg)',
        fontWeight: 600,
        color: 'var(--text-primary)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
    },
    editableContent: {
        padding: 'var(--spacing-lg)',
        color: 'var(--text-primary)',
        lineHeight: 1.6,
        fontSize: 'var(--font-size-md)',
    },
    richTextH: {
        color: 'var(--text-primary)',
        marginTop: 'var(--spacing-md)',
        marginBottom: 'var(--spacing-sm)',
    },
    richTextHFirst: {
        marginTop: 0,
    },
    richTextP: {
        marginBottom: 'var(--spacing-sm)',
        lineHeight: 1.7,
    },
    richTextList: {
        marginLeft: 'var(--spacing-lg)',
        marginBottom: 'var(--spacing-sm)',
    },
    richTextItem: {
        marginBottom: 'var(--spacing-xs)',
    },
    richTextQuote: {
        borderLeftWidth: '4px',
        borderLeftStyle: 'solid',
        borderLeftColor: 'var(--primary)',
        paddingLeft: 'var(--spacing-md)',
        marginBlock: 'var(--spacing-md)',
        fontStyle: 'italic',
        color: 'var(--text-secondary)',
    },
    richTextCode: {
        backgroundColor: 'var(--bg-element)',
        paddingBlock: '2px',
        paddingInline: '6px',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.9em',
    },
    richTextPre: {
        backgroundColor: 'var(--bg-element)',
        padding: 'var(--spacing-md)',
        borderRadius: 'var(--radius-md)',
        overflowX: 'auto',
        marginBlock: 'var(--spacing-md)',
    },
    metadataList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-sm)',
    },
    metadataItem: {
        fontSize: 'var(--font-size-sm)',
    },
    metadataStrong: {
        color: 'var(--primary)',
        fontWeight: 600,
        marginRight: 'var(--spacing-xs)',
        display: 'inline-block',
    },
    sectionWrapper: {
        borderRadius: 'var(--radius-sm)',
        backgroundColor: 'var(--bg-card)',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        overflow: 'hidden',
        position: 'relative',
    },
    sectionContent: {
        overflow: 'hidden',
        transitionProperty: 'max-height',
        transitionDuration: '0.5s',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    sectionContentCollapsed: {
        maxHeight: 0,
        paddingTop: 0,
        paddingBottom: 0,
    },
    mediaContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-md)',
        padding: 'var(--spacing-md)',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    mediaScroll: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-lg)',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    mediaBox: {
        width: '100%',
        borderRadius: 'var(--radius-md)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        backgroundColor: 'var(--bg-card)',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
        animationName: fadeSlideIn,
        animationDuration: '0.5s',
        animationTimingFunction: 'ease-out',
        transitionProperty: 'all',
        transitionDuration: '0.3s',
        transitionTimingFunction: 'ease',
    },
    mediaAddButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '64px',
        height: '64px',
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--bg-element)',
        borderWidth: '2px',
        borderStyle: 'dashed',
        borderColor: 'var(--border)',
        cursor: 'pointer',
        transitionProperty: 'all',
        transitionDuration: '0.3s',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        ':hover': {
            backgroundColor: 'var(--bg-hover)',
            borderColor: 'currentColor',
            transform: 'scale(1.05)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
        },
        ':active': {
            transform: 'scale(0.98)',
        },
        '::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at center, currentColor 0%, transparent 70%)',
            opacity: {
                default: 0,
                ':hover': 0.1,
            },
            transitionProperty: 'opacity',
            transitionDuration: '0.3s',
            borderRadius: 'inherit',
        },
    },
    uploadButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        paddingBlock: 'var(--spacing-md)',
        paddingInline: 'var(--spacing-md)',
        backgroundImage: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: 'white',
        borderWidth: 0,
        borderRadius: 'var(--radius-lg)',
        fontSize: 'var(--font-size-md)',
        fontWeight: 600,
        cursor: 'pointer',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        boxShadow: {
            default: 'var(--shadow-glass)',
            ':hover': 'var(--shadow-glass-hover)',
        },
        position: 'relative',
        overflow: 'hidden',
        minWidth: '140px',
        justifyContent: 'center',
        transform: {
            default: 'translateY(0)',
            ':hover': 'translateY(-2px)',
            ':active': 'translateY(0)',
        },
        '::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            backgroundImage: 'linear-gradient(90deg, transparent, var(--shimmer-light), transparent)',
            transitionProperty: 'left',
            transitionDuration: '0.5s',
            transitionTimingFunction: 'ease',
        },
        ':hover::before': {
            left: '100%',
        }
    },
    mediaContent: {
        flexGrow: 1,
        padding: '16px',
        overflowX: 'auto',
        backgroundColor: 'var(--bg-card)',
        minHeight: '200px',
        '::-webkit-scrollbar': {
            height: '8px',
        },
        '::-webkit-scrollbar-track': {
            backgroundColor: 'var(--scrollbar-track-color)',
            borderRadius: '10px',
        },
        '::-webkit-scrollbar-thumb': {
            backgroundColor: 'var(--scrollbar-thumb-color)',
            borderRadius: '10px',
        }
    },
    mediaItemsContainer: {
        display: 'flex',
        gap: '16px',
        paddingBottom: '8px',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
    },
    taskImage: {
        width: '100%',
        height: 'auto',
        maxHeight: '200px',
        display: 'block',
        objectFit: 'cover',
        transitionProperty: 'transform',
        transitionDuration: '0.3s',
        borderRadius: 'var(--radius-md)',
    },
    taskVideo: {
        width: '100%',
        borderRadius: '8px',
        maxHeight: '200px',
        objectFit: 'contain',
    },
    taskCard: {
        flex: '0 0 250px',
        minWidth: '200px',
        maxWidth: '250px',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--bg-element)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        overflow: 'hidden',
        transitionProperty: 'all',
        transitionDuration: '0.3s',
        cursor: 'default',
        display: 'flex',
        flexDirection: 'column',
        ':hover': {
            borderColor: 'var(--primary-light)',
            boxShadow: 'var(--shadow-md)',
        }
    },
    menuDropdown: {
        position: 'absolute',
        top: '100%',
        right: 0,
        backgroundColor: 'var(--bg-card)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        minWidth: '140px',
        paddingBlock: '4px',
        marginTop: '4px',
    },
    pulsing: {
        animationName: pulse,
        animationDuration: '1.5s',
        animationIterationCount: 'infinite',
    },
    spinningIcon: {
        animationName: spin,
        animationDuration: '1.5s',
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        borderRadius: '8px',
        paddingBlock: '0.5rem',
        paddingInline: '0.75rem',
        minWidth: {
            default: '500px',
            [TABLET]: '0',
        },
        width: '100%',
        maxWidth: '500px',
        position: 'relative',
        transitionProperty: 'border-color',
        transitionDuration: '0.2s',
    },
    mediaTab: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-md)',
        paddingBlock: 'var(--spacing-sm)',
        paddingInline: 'var(--spacing-lg)',
        backgroundColor: 'var(--bg-card)',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        borderBottomWidth: 0,
        borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
        cursor: 'pointer',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        minHeight: '48px',
        minWidth: {
            default: '280px',
            [TABLET]: '200px',
        },
        color: 'var(--text-secondary)',
        fontWeight: 500,
        transform: {
            default: 'translateY(0)',
            ':hover': 'translateY(-2px)',
        }
    },
    mediaTabActive: {
        backgroundColor: 'var(--bg-primary)',
        borderColor: 'var(--primary)',
        color: 'var(--primary)',
        transform: 'translateY(-2px)',
        boxShadow: 'var(--shadow-glass)',
    },
    shineHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBlock: '1.25rem',
        paddingInline: '1.75rem',
        backgroundImage: 'linear-gradient(45deg, var(--bg-secondary) 0%, var(--bg-secondary) 85%, var(--primary-light) 150%)',
        backgroundSize: '200% 100%',
        transitionProperty: 'background-position, box-shadow',
        transitionDuration: '0.3s',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'var(--border)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        ':hover': {
            backgroundPosition: 'right',
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
            borderBottomColor: 'var(--accent-light)',
        },
        '::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            // Using strings for dynamic mouse vars is standard in StyleX
            backgroundImage: 'radial-gradient(circle 500px at var(--mouse-x, 0) var(--mouse-y, 0), rgba(var(--primary-light-rgb, 120, 144, 240), 0.15) 0%, rgba(var(--primary-light-rgb, 120, 144, 240), 0.08) 50%, transparent 90%)',
            opacity: {
                default: 0,
                ':hover': 1,
            },
            transitionProperty: 'opacity',
            transitionDuration: '0.3s',
            pointerEvents: 'none',
            zIndex: 1,
        }
    },
    addButtonContainer: {
        flexShrink: 0,
    },
    mediaRow: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    mediaIconContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    plusIcon: {
        position: 'absolute',
        top: '-4px',
        right: '-4px',
        backgroundColor: 'var(--bg-card)',
        borderRadius: '50%',
        color: 'currentColor',
    },
    taskAudio: {
        width: '100%',
        marginTop: 'auto',
    },
    mediaPlaceholder: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '150px',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--text-tertiary)',
        fontSize: 'var(--font-size-sm)',
    },
    taskHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'var(--border)',
    },
    taskTypeAndStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    taskTypeIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    taskActions: {
        display: 'flex',
        alignItems: 'center',
    },
    taskControlButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px',
        backgroundColor: 'transparent',
        borderWidth: 0,
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        borderRadius: 'var(--radius-sm)',
        transitionProperty: 'background-color, color',
        transitionDuration: '0.2s',
        ':hover': {
            backgroundColor: 'var(--bg-hover)',
            color: 'var(--text-primary)',
        },
    },
    taskBody: {
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flexGrow: 1,
    },
    mediaItemPrompt: {
        fontSize: 'var(--font-size-xs)',
        color: 'var(--text-secondary)',
        margin: 0,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
});